import { useState, useEffect, useMemo } from 'react';
import { DataSet, DerivedDataSet } from '../../types';
import { DataAccessor, DataState } from '../../types/data-accessor.types';
import { getDataAccessor } from '../utils/createDataAccessor';
import { useProjectStore } from '../stores/projectStore';
import logger from '../utils/logger';

/**
 * データセットとそのデータアクセサを管理するカスタムフック
 * PrimaryDataSetとDerivedDataSetの違いを吸収し、統一的なインターフェースを提供
 */
export interface UseDatasetWithDataResult {
  /**
   * データアクセサ
   */
  accessor: DataAccessor | null;
  
  /**
   * データの状態
   */
  dataState: DataState;
  
  /**
   * データがロード中かどうか
   */
  isLoading: boolean;
  
  /**
   * エラー情報
   */
  error: Error | null;
  
  /**
   * データが利用可能かどうか
   */
  isDataAvailable: boolean;
  
  /**
   * データの再ロードを強制
   */
  reload: () => Promise<void>;
}

/**
 * データセットのデータを自動的にロードし、アクセサを提供するフック
 * @param dataset 対象のデータセット
 * @param options オプション
 * @returns データアクセサと状態情報
 */
export function useDatasetWithData(
  dataset: DataSet | null | undefined,
  options: {
    onError?: (error: Error) => void;
  } = {}
): UseDatasetWithDataResult {
  const [dataState, setDataState] = useState<DataState>('not-loaded');
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // オプションの個別の値を取得
  const { onError } = options;

  // データアクセサの作成（メモ化）
  // autoLoadは常にtrueとして動作（useEffect内でensureDataLoadedを呼ぶため）
  const accessor = useMemo(() => {
    if (!dataset) return null;

    return getDataAccessor(dataset, {
      autoLoad: true,
      onError: (err) => {
        setError(err);
        if (onError) {
          onError(err);
        }
      }
    });
  }, [dataset, onError]);

  // データのロード処理
  useEffect(() => {
    if (!accessor) {
      setDataState('not-loaded');
      setIsLoading(false);
      setError(null);
      return;
    }

    // 初期状態を設定
    setDataState(accessor.getDataState());

    // データが既にロード済みの場合
    if (accessor.isDataAvailable()) {
      setIsLoading(false);
      setDataState('loaded');
      return;
    }

    // データのロード
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    accessor.ensureDataLoaded()
      .then((updatedDataset) => {
        if (!cancelled) {
          // DerivedDataSetの場合、ストアを更新
          if (dataset?.type === 'derived' && updatedDataset) {
            const { updateDataset } = useProjectStore.getState();
            updateDataset(dataset.id, () => updatedDataset as DerivedDataSet);
          }

          setDataState('loaded');
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setDataState('error');
          setIsLoading(false);
          setError(err);
          logger.error('[useDatasetWithData] Failed to load data for dataset:', dataset?.id, err);
        }
      });

    // クリーンアップ
    return () => {
      cancelled = true;
    };
  }, [accessor, dataset?.id, dataset?.data, dataset?.type]); // typeも依存配列に追加

  // 再ロード関数
  const reload = async () => {
    if (!accessor) return;

    setIsLoading(true);
    setError(null);
    setDataState('loading');

    try {
      const updatedDataset = await accessor.ensureDataLoaded();

      // DerivedDataSetの場合、ストアを更新
      if (dataset?.type === 'derived' && updatedDataset) {
        const { updateDataset } = useProjectStore.getState();
        updateDataset(dataset.id, () => updatedDataset as DerivedDataSet);
      }

      setDataState('loaded');
    } catch (err) {
      setDataState('error');
      setError(err as Error);
      logger.error('[useDatasetWithData] Failed to reload data for dataset:', dataset?.id, err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    accessor,
    dataState,
    isLoading,
    error,
    isDataAvailable: dataState === 'loaded' && !!accessor?.isDataAvailable(),
    reload
  };
}